import {CrownFilled} from '@ant-design/icons'
import {Col, Row, Spin} from 'antd'
import {member_getMembers_Api} from 'apis'
import {organizationModel, userModel} from 'apis/model'
import _ from 'lodash'
import useAsyncWithCache from 'modules/asyncCache/useAsyncWithCache'
import Translate from 'modules/local/Translate'
import useTranslate from 'modules/local/useTranslate'
import React, {useCallback, useContext, useMemo} from 'react'
import {Link} from 'react-router-dom'
import OrganizationChannels from 'views/Organization/components/OrganizationChannels'
import OrganizationFeeds, {OrganizationFeedsByContentType,} from 'views/Organization/components/OrganizationFeeds'
import OrganizationMeta from 'views/Organization/components/OrganizationMeta'
import {OrganizationContext} from 'views/Organization/OrganizationContext'
import ServerContext from 'views/Server/ServerContext'
import {fullGrid, LayoutBuilder, renderSelf, twoThirdGrid} from 'views/Shared'
import TooltipApp from "components/TooltipApp";

const renderMember =
  (id) =>
    ({role_id, user}, index) => {
      const {name, username} = user

      const isOwner = role_id === 1

      const detailLink = userModel.getLinkToDetail(user)

      return (
        <Translate key={index}>
          {(t) => (
            <Col className="p-col-0 mb-3">
              <Link
                className="flex font-bold content-center"
                to={detailLink}>
                {name}
                {isOwner && (
                  <TooltipApp title={t('owner')}>
                    <div className="pl-3 my-auto flex text-yellow-500">
                      <CrownFilled/>
                    </div>
                  </TooltipApp>
                )}
              </Link>
              <Link
                className="flex font-light italic text-xs"
                to={detailLink}>
                {username}
              </Link>
            </Col>
          )}
        </Translate>
      )
    }

const OrganizationMembers = ({id, type}) => {
  const t = useTranslate()

  const {response} = useAsyncWithCache({
    cacheId: member_getMembers_Api.path
      .replace(':prop', type)
      .replace(':id', id),
    apiInfo: member_getMembers_Api,
    query: {
      ':id': id,
      prop: type,
    },
    values: {
      role_id: '1,2',
    },
  })

  const members = useMemo(() => {
    const data = _.get(response, 'data.data', []).filter((e) => !!e.visible)
    return _.orderBy(data, ['role_id'], 'asc')
  }, [response])

  return (
    !!members.length && (
      <Col className="pt-3 p-col-0">
        <p className="font-semibold uppercase text-color-300 pb-3">
          {t('admin')}
        </p>
        <div className="px-3">{members.map(renderMember(id))}</div>
      </Col>
    )
  )
}

export const OrganizationSideBar = () => {
  const {item} = useContext(ServerContext)
  const id = organizationModel.getId(item)
  const type = organizationModel.getType(item)
  return (
    <Col className="space-y-3 mt-3 px-3 md:p-0">
      <OrganizationMeta/>
      <OrganizationMembers {...{id, type}} />
      <OrganizationChannels/>
    </Col>
  )
}

const OrganizationHome = ({title, renderHeader = renderSelf, ...props}) => {
  const leftGrid = twoThirdGrid
  const rightGrid = fullGrid - leftGrid

  const {organization} = useContext(OrganizationContext)

  const id = organizationModel.getId(organization)

  const type = organizationModel.getType(organization)

  const Feeds = useCallback(
    () => (
      <OrganizationFeedsByContentType
        className="p-3"
        renderItem={(props) => <OrganizationFeeds {...props} />}
        {...{id, type}}
      />
    ),
    [id]
  )

  return (
    <Spin
      spinning={_.isEmpty(organization)}
      className="p-3 space-y-6">
      {renderHeader(title)}

      <LayoutBuilder
        value={({isSm}) => !!isSm}
        params={{
          className: 'space-3',
          justify: 'space-between',
        }}
        Wrapper={Row}
        builder={({params, Wrapper}) => (
          <Wrapper {...params}>
            <Col flex={1}>
              <OrganizationSideBar/>
            </Col>
            <Col flex={1}>
              <Feeds/>
            </Col>
          </Wrapper>
        )}
        orElse={({params, Wrapper}) => (
          <Wrapper
            {...params}
            gutter={[16, 16]}>
            <Col lg={leftGrid}>
              <Feeds/>
            </Col>
            <Col lg={rightGrid}>
              <OrganizationSideBar/>
            </Col>
          </Wrapper>
        )}
      />
    </Spin>
  )
}

export default OrganizationHome
